import { mapValues } from "utils/ObjectUtils";
import {
  FETCH_TOP_CATEGORIES,
  FETCH_CHILD_CATEGORIES,
  FETCH_PARENT_CHILD_CATEGORIES,
  RESET_CATEGORY,
  CategoryActionTypes,
  LoadTopCategoriesResponseAction,
  LoadChildCategoriesResponseAction,
  AttributeData,
  Translations,
  SetAdditionalAttributesAction,
  SET_ADDITIONAL_ATTRIBUTES,
  SET_CURRENT_CATEGORY,
  SetCurrentCategoryAction,
  FETCH_TOP_CATEGORIES_FOR_REPORT,
  FETCH_CHILD_CATEGORIES_FOR_REPORT,
  RESET_TOP_CATEGORIES_LOADING_STATE_VALUE,
  UPDATE_TOP_CATEGORIES_LOADING_STATE_VALUE,
  FETCH_TOP_CATEGORIES_FOR_FIND_PRODUCTS,
  FETCH_CHILD_CATEGORIES_FOR_FIND_PRODUCTS,
  SET_CATEGORY_EXPAND,
  RESET_CATEGORY_EXPAND,
  LOAD_TOP_CATEGORIES,
  LOAD_TOP_CATEGORIES_FOR_FIND_PRODUCTS,
  SET_TOP_CATEGORIES_PROGRESS_STATE,
  FETCH_CATEGORY_DETAILS_BY_ID,
  LoadCategoryDetailsByIdResponseAction,
  FETCH_PRODUCT_COUNT,
  LoadProductCountSuccessAction,
  LoadProductCountFailureAction,
} from "./CategoryTypes";

export interface CategoryState {
  topCategory: Category[];
  childCategory: ChildCategory;
  attributes: AttributeData[];
  unassignedAttributes: AttributeData[];
  variantAttributes: AttributeData[];
  currentCategory: string;
  topCategoriesFetched: boolean;
  findProductsModalTopCategory: {
    categoryId: string;
    childCategoryCount: number;
    productCount: number;
    translations: Translations[];
    name: string;
    code: string;
    isPublished: boolean;
  }[];
  findProductsModalChildCategory: ChildCategory;
  topCategoriesForFindProductsFetched: boolean;
  isCatalogOrStoreChanged: boolean;
  isTopCategoriesLoading: boolean;
  categoryDetails: {
    [categoryId: string]: {
      productCount?: number;
    };
  };
}
export interface ChildCategory {
  [key: string]: {
    childCategories: Category[];
  };
}
export interface Category {
  parentId?: string;
  categoryId: string;
  childCategoryCount: number;
  translations: Translations[];
  productCount?: number;
  isProductCountLoading?: boolean;
  code: string;
  name: string;
  isPublished: boolean;
  isAutomatedCollection?: boolean;
}

const initialState: CategoryState = {
  topCategory: [],
  childCategory: {},
  attributes: [],
  variantAttributes: [],
  unassignedAttributes: [],
  currentCategory: "",
  topCategoriesFetched: false,
  findProductsModalTopCategory: [],
  findProductsModalChildCategory: {},
  topCategoriesForFindProductsFetched: false,
  isCatalogOrStoreChanged: false,
  isTopCategoriesLoading: false,
  categoryDetails: {},
};

export const CategoryReducer = (
  state = initialState,
  action: CategoryActionTypes,
): CategoryState => {
  switch (action.type) {
    case FETCH_PRODUCT_COUNT.SUCCESS: {
      const productCountAction = action as LoadProductCountSuccessAction;
      return {
        ...state,
        topCategory: state.topCategory.map((cat) => {
          if (cat.categoryId === productCountAction.meta.categoryId) {
            return {
              ...cat,
              productCount: productCountAction.payload.productCount,
              isProductCountLoading: false,
            };
          }
          return cat;
        }),
        childCategory: mapValues(state.childCategory, (cat) => {
          return {
            childCategories: cat.childCategories.map((childCat) => {
              if (childCat.categoryId === productCountAction.meta.categoryId) {
                return {
                  ...childCat,
                  productCount: productCountAction.payload.productCount,
                  isProductCountLoading: false,
                };
              }
              return childCat;
            }),
          };
        }),
      };
    }
    case FETCH_PRODUCT_COUNT.FAILURE: {
      const productCountAction = action as LoadProductCountFailureAction;
      return {
        ...state,
        topCategory: state.topCategory.map((cat) => {
          if (cat.categoryId === productCountAction.meta.categoryId) {
            return {
              ...cat,
              isProductCountLoading: false,
            };
          }
          return cat;
        }),
        childCategory: mapValues(state.childCategory, (cat) => {
          return {
            childCategories: cat.childCategories.map((childCat) => {
              if (childCat.categoryId === productCountAction.meta.categoryId) {
                return {
                  ...childCat,
                  isProductCountLoading: false,
                };
              }
              return childCat;
            }),
          };
        }),
      };
    }
    case FETCH_TOP_CATEGORIES_FOR_REPORT.SUCCESS: {
      const topCategoriesAction = action as LoadTopCategoriesResponseAction;
      return {
        ...state,
        topCategory: topCategoriesAction.payload.results.map((cat) => ({
          categoryId: cat.categoryId,
          childCategoryCount: cat.childCategoryCount,
          productCount: cat.productCount,
          isProductCountLoading: topCategoriesAction.meta?.useProductCountAPI
            ? true
            : false,
          translations: cat.translations,
          code: cat.code,
          name: cat.name,
          isPublished: cat.isPublished,
        })),
      };
    }
    case FETCH_TOP_CATEGORIES.FAILURE:
      return {
        ...state,
        isTopCategoriesLoading: false,
      };
    case FETCH_TOP_CATEGORIES.SUCCESS:
      const topCategoriesAction = action as LoadTopCategoriesResponseAction;
      let topCats: any = [];
      for (const v of topCategoriesAction.payload.results) {
        topCats.push({
          categoryId: v.categoryId,
          childCategoryCount: v.childCategoryCount,
          productCount: topCategoriesAction.meta?.useProductCountAPI
            ? undefined
            : v.productCount,
          isProductCountLoading: topCategoriesAction.meta?.useProductCountAPI
            ? true
            : false,
          translations: v.translations,
          code: v.code,
          name: v.name,
          isPublished: v.isPublished,
          isAutomatedCollection: v?.isAutomatedCollection,
        });
      }
      // Also update Find Products modal top categories with product counts
      const updatedFindProductsTopCategory = state.findProductsModalTopCategory.map(
        (cat) => {
          const updatedCat = topCats.find(
            (tc: Category) => tc.categoryId === cat.categoryId,
          );
          if (updatedCat && updatedCat.productCount !== undefined) {
            return {
              ...cat,
              productCount: updatedCat.productCount,
            };
          }
          return cat;
        },
      );
      return {
        ...state,
        topCategory: topCats,
        findProductsModalTopCategory: updatedFindProductsTopCategory,
        topCategoriesFetched: true,
        isTopCategoriesLoading: false,
      };
    case FETCH_CHILD_CATEGORIES_FOR_REPORT.SUCCESS: {
      const childCategoriesAction = action as LoadChildCategoriesResponseAction;
      const categoryResults = childCategoriesAction.payload.results;
      const isTopCategoryExists = categoryResults?.some(
        (e) => e.parentId === "root",
      );
      const categoryId: string = !isTopCategoryExists
        ? categoryResults[0].categoryId
        : "";
      const childCategoriesResults = isTopCategoryExists
        ? categoryResults.filter((e) => e.parentId !== "root")
        : categoryResults?.some((e) => categoryId === e.parentId)
          ? categoryResults?.filter((e) => categoryId === e.parentId)
          : categoryResults;

      const parentCategoryId = childCategoriesResults[0].parentId;
      return {
        ...state,
        childCategory: {
          ...state.childCategory,
          [parentCategoryId]: {
            childCategories: childCategoriesResults.map((node) => ({
              categoryId: node.categoryId,
              childCategoryCount: node.childCategoryCount,
              translations: node.translations,
              productCount: node.productCount,
              code: node.code,
              name: node.name,
              isPublished: node.isPublished,
            })),
          },
        },
      };
    }
    case FETCH_CHILD_CATEGORIES.SUCCESS:
      const childCategoriesAction = action as LoadChildCategoriesResponseAction;
      const parentCategoryId = childCategoriesAction.payload.categoryId;
      let childCat: any = [];
      const categoryResults = childCategoriesAction.payload.results ?? [];
      const isTopCategoryExists = categoryResults?.some(
        (e) => e.parentId === "root",
      );
      const categoryId: string = !isTopCategoryExists
        ? categoryResults?.[0]?.categoryId
        : "";
      const childCategoriesResults = isTopCategoryExists
        ? categoryResults?.filter((e) => e.parentId !== "root")
        : categoryResults?.some((e) => categoryId === e.parentId)
          ? categoryResults?.filter((e) => categoryId === e.parentId)
          : categoryResults;

      for (const v of childCategoriesResults) {
        childCat.push({
          categoryId: v.categoryId,
          childCategoryCount: v.childCategoryCount,
          productCount: childCategoriesAction.meta?.useProductCountAPI
            ? undefined
            : v.productCount,
          isProductCountLoading: childCategoriesAction.meta?.useProductCountAPI
            ? true
            : false,
          translations: v.translations,
          code: v.code,
          name: v.name,
          isPublished: v.isPublished,
          parentId: v.parentId,
        });
      }
      const existingChildCategories =
        state.childCategory?.[childCat?.[0]?.parentId]?.childCategories ?? [];
      const mergedChildCategories =
        existingChildCategories?.map((existing) => {
          const updated = childCat.find(
            (cat: Category) => cat.categoryId === existing.categoryId,
          );
          return updated || existing;
        }) ?? [];

      const newCategories = childCat?.filter(
        (cat: Category) =>
          !existingChildCategories.some(
            (existing) => existing.categoryId === cat.categoryId,
          ),
      );

      const childCategoryData = childCat[0]
        ? {
            ...state.childCategory,
            [parentCategoryId]: {
              childCategories: [...mergedChildCategories, ...newCategories],
            },
          }
        : {
            ...state.childCategory,
          };
      // Also update Find Products modal child categories with product counts
      const updatedFindProductsChildCategory = { ...state.findProductsModalChildCategory };
      if (childCat[0] && parentCategoryId) {
        const findProductsParentData = state.findProductsModalChildCategory[
          parentCategoryId
        ];
        if (findProductsParentData) {
          const updatedFindProductsChildCats = findProductsParentData.childCategories.map(
            (existing) => {
              const updated = childCat.find(
                (cat: Category) => cat.categoryId === existing.categoryId,
              );
              if (updated && updated.productCount !== undefined) {
                return {
                  ...existing,
                  productCount: updated.productCount,
                };
              }
              return existing;
            },
          );
          updatedFindProductsChildCategory[parentCategoryId] = {
            childCategories: updatedFindProductsChildCats,
          };
        }
      }
      return {
        ...state,
        childCategory: childCategoryData,
        findProductsModalChildCategory: updatedFindProductsChildCategory,
      };
    case FETCH_PARENT_CHILD_CATEGORIES.SUCCESS:
      const parentChildCategoriesAction =
        action as LoadChildCategoriesResponseAction;
      let parentChildCat: any = [];
      const parentChildCategoriesResults =
        parentChildCategoriesAction.payload.results;
      for (const v of parentChildCategoriesResults) {
        parentChildCat.push({
          categoryId: v.categoryId,
          childCategoryCount: v.childCategoryCount,
          productCount: v.productCount,
          translations: v.translations,
          code: v.code,
          name: v.name,
          isPublished: v.isPublished,
          parentId: v.parentId,
        });
      }

      const filteredParentChildCat = state.childCategory[
        parentChildCat?.[0]?.parentId
      ]?.childCategories?.map((childCategoryData) => {
        let parentChildCategoryData: any = {};
        for (const childCat of parentChildCat) {
          if (childCat.categoryId === childCategoryData.categoryId) {
            parentChildCategoryData = childCat;
            return parentChildCategoryData;
          }
        }
        if (Object.keys(parentChildCategoryData).length === 0) {
          return childCategoryData;
        } else {
          return parentChildCategoryData;
        }
      });
      const childCategory = filteredParentChildCat
        ? {
            ...state.childCategory,
            [parentChildCat[0].parentId]: {
              childCategories: filteredParentChildCat,
            },
          }
        : {
            //if the category doesnt have child categories & the parent of this category doesnt saved in redux
            //then we are just loading previous redux state. When we open that category it will load the data
            ...state.childCategory,
          };
      return {
        ...state,
        childCategory,
      };
    case SET_ADDITIONAL_ATTRIBUTES:
      const additionalAttributesAction =
        action as SetAdditionalAttributesAction;
      const copiedAttributes = state.unassignedAttributes.slice();
      copiedAttributes.push(
        ...additionalAttributesAction.payload.additionalAttributesData,
      );
      return {
        ...state,
        unassignedAttributes: copiedAttributes,
      };
    case SET_CURRENT_CATEGORY:
      const categoryAction = action as SetCurrentCategoryAction;
      return {
        ...state,
        currentCategory: categoryAction.payload.categoryId,
      };
    case RESET_TOP_CATEGORIES_LOADING_STATE_VALUE:
      return {
        ...state,
        topCategoriesFetched: false,
      };
    case LOAD_TOP_CATEGORIES:
      return {
        ...state,
        topCategoriesFetched: false,
        isTopCategoriesLoading: true,
      };
    case UPDATE_TOP_CATEGORIES_LOADING_STATE_VALUE:
      return {
        ...state,
        topCategoriesFetched: true,
      };
    case RESET_CATEGORY:
      return initialState;
    case LOAD_TOP_CATEGORIES_FOR_FIND_PRODUCTS: {
      return {
        ...state,
        topCategoriesForFindProductsFetched: false,
      };
    }
    case FETCH_TOP_CATEGORIES_FOR_FIND_PRODUCTS.REQUEST: {
      return {
        ...state,
        topCategoriesForFindProductsFetched: false,
      };
    }
    case FETCH_TOP_CATEGORIES_FOR_FIND_PRODUCTS.SUCCESS: {
      const topCategoriesAction = action as LoadTopCategoriesResponseAction;
      return {
        ...state,
        topCategoriesForFindProductsFetched: true,
        findProductsModalTopCategory: topCategoriesAction.payload.results.map(
          (cat) => ({
            categoryId: cat.categoryId,
            childCategoryCount: cat.childCategoryCount,
            productCount: cat.productCount,
            translations: cat.translations,
            code: cat.code,
            name: cat.name,
            isPublished: cat.isPublished,
          }),
        ),
      };
    }
    case FETCH_CHILD_CATEGORIES_FOR_FIND_PRODUCTS.SUCCESS:
      const childCategoriesForFindProductsAction =
        action as LoadChildCategoriesResponseAction;
      let childCatsForFindProducts: any = [];
      const categories =
        childCategoriesForFindProductsAction.payload.results ?? [];
      const isTopCategoryAvailable = categories?.some(
        (e) => e.parentId === "root",
      );
      const category: string = !isTopCategoryAvailable
        ? categories[0].categoryId
        : "";
      const childCategoriesForFindProductsResults = isTopCategoryAvailable
        ? categories?.filter((e) => e.parentId !== "root")
        : categories?.some((e) => category === e.parentId)
          ? categories?.filter((e) => category === e.parentId)
          : categories;

      for (const v of childCategoriesForFindProductsResults) {
        childCatsForFindProducts.push({
          categoryId: v.categoryId,
          childCategoryCount: v.childCategoryCount,
          productCount: v.productCount,
          translations: v.translations,
          code: v.code,
          name: v.name,
          isPublished: v.isPublished,
          parentId: v.parentId,
        });
      }
      const existingCategories =
        state.childCategory?.[childCatsForFindProducts?.[0]?.parentId]
          ?.childCategories ?? [];
      const mergedCategories =
        existingCategories?.map((existing) => {
          const updated = childCatsForFindProducts.find(
            (cat: Category) => cat.categoryId === existing.categoryId,
          );
          return updated || existing;
        }) ?? [];

      const newChildCategories = childCatsForFindProducts?.filter(
        (cat: Category) =>
          !existingCategories.some(
            (existing) => existing.categoryId === cat.categoryId,
          ),
      );
      return {
        ...state,
        findProductsModalChildCategory: {
          ...state.findProductsModalChildCategory,
          [childCategoriesForFindProductsResults[0].parentId]: {
            childCategories: [...mergedCategories, ...newChildCategories],
          },
        },
      };
    case SET_CATEGORY_EXPAND:
      return {
        ...state,
        isCatalogOrStoreChanged: true,
      };
    case RESET_CATEGORY_EXPAND:
      return {
        ...state,
        isCatalogOrStoreChanged: false,
        topCategory: [],
      };
    case SET_TOP_CATEGORIES_PROGRESS_STATE:
      return {
        ...state,
        isTopCategoriesLoading: true,
      };
    case FETCH_CATEGORY_DETAILS_BY_ID.SUCCESS:
      const categoryDetails = action as LoadCategoryDetailsByIdResponseAction;
      const detailsCategoryId = categoryDetails.payload?.categoryId;
      const newProductCount = categoryDetails.meta.useProductCountAPI
        ? undefined
        : categoryDetails.payload?.productCount;

      // Get current toolbar count (from categoryDetails)
      const currentToolbarCount =
        state.categoryDetails[detailsCategoryId]?.productCount;

      // Calculate the difference between tree count and toolbar count
      // This difference should be maintained when toolbar count changes
      const getUpdatedProductCount = (
        currentTreeCount: number | undefined,
      ): number | undefined => {
        // Calculate difference: treeCount - toolbarCount
        // When toolbar count changes, update tree count by maintaining the difference
        if (
          currentTreeCount !== undefined &&
          currentToolbarCount !== undefined &&
          newProductCount !== undefined
        ) {
          const difference = currentTreeCount - currentToolbarCount;
          return newProductCount + difference;
        }

        // If we don't have both counts, just use the new count
        return currentTreeCount;
      };

      return {
        ...state,
        topCategory: state.topCategory.map((cat) => {
          if (cat.categoryId === detailsCategoryId) {
            return {
              ...cat,
              productCount: getUpdatedProductCount(cat.productCount),
              isProductCountLoading: categoryDetails.meta.useProductCountAPI
                ? true
                : false,
            };
          }
          return cat;
        }),
        childCategory: mapValues(state.childCategory, (cat) => {
          return {
            childCategories: cat.childCategories.map((childCat) => {
              if (childCat.categoryId === detailsCategoryId) {
                return {
                  ...childCat,
                  productCount: getUpdatedProductCount(childCat.productCount),
                  isProductCountLoading: categoryDetails.meta.useProductCountAPI
                    ? true
                    : false,
                };
              }
              return childCat;
            }),
          };
        }),
        findProductsModalTopCategory: state.findProductsModalTopCategory.map(
          (cat) => {
            if (cat.categoryId === detailsCategoryId) {
              return {
                ...cat,
                productCount: newProductCount !== undefined
                  ? newProductCount
                  : cat.productCount,
              };
            }
            return cat;
          },
        ),
        findProductsModalChildCategory: mapValues(
          state.findProductsModalChildCategory,
          (cat) => {
            return {
              childCategories: cat.childCategories.map((childCat) => {
                if (childCat.categoryId === detailsCategoryId) {
                  return {
                    ...childCat,
                    productCount: newProductCount !== undefined
                      ? newProductCount
                      : childCat.productCount,
                  };
                }
                return childCat;
              }),
            };
          },
        ),
        categoryDetails: {
          ...state.categoryDetails,
          [detailsCategoryId]: {
            productCount: newProductCount,
          },
        },
      };
    default:
      return state;
  }
};
